import React from 'react';
import { Shield, Zap, Info } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="bg-stone-50 shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-center">
          <Info className="h-6 w-6 text-purple-600 mr-2" />
          <h3 className="text-lg leading-6 font-medium text-gray-900">About RedVsBlue</h3>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Cybersecurity tools knowledge repository
        </p>
      </div>
      <div className="border-t border-gray-200">
        <dl>
          <div className="bg-stone-100 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Purpose</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              RedVsBlue is an educational resource designed to showcase the tools, techniques, and methodologies used by both offensive (Red Team) and defensive (Blue Team) security professionals. Our goal is to provide a comprehensive overview of the cybersecurity landscape to help security practitioners, students, and enthusiasts understand the full spectrum of security operations.
            </dd>
          </div>
          
          <div className="bg-stone-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Red Team Focus</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <div className="flex items-center mb-2">
                <Zap className="h-5 w-5 text-red-600 mr-2" />
                <span className="font-medium">Offensive Security</span>
              </div>
              Our Red Team section catalogs tools used for reconnaissance, exploitation, privilege escalation, persistence, and data exfiltration. Understanding these tools and techniques is essential for security professionals to effectively test and improve organizational defenses.
            </dd>
          </div>
          
          <div className="bg-stone-100 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Blue Team Focus</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <div className="flex items-center mb-2">
                <Shield className="h-5 w-5 text-blue-600 mr-2" />
                <span className="font-medium">Defensive Security</span>
              </div>
              Our Blue Team section showcases tools for monitoring, intrusion detection, defense, forensics, threat intelligence, and security orchestration. These tools are critical for organizations to detect, respond to, and mitigate security threats effectively.
            </dd>
          </div>
          
          <div className="bg-stone-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Disclaimer</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              The information provided on this site is for educational purposes only. RedVsBlue does not encourage or promote any illegal activities. All security testing should be performed only with proper authorization on systems you own or have permission to test.
            </dd>
          </div>

          <div className="bg-stone-100 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Our Network</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <p className="mb-3">RedVsBlue is part of a network of cybersecurity resources designed to help security professionals and organizations:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><a href="https://tooling.hackernoob.tips/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">Cybersecurity Tools Directory</a> - A comprehensive index of security tools and resources</li>
                <li><a href="https://directory.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">CISO Marketplace Directory</a> - A directory of trusted MSPs, MSSPs, and security service providers</li>
                <li><a href="https://www.hackernoob.tips/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">HackerNoob.Tips</a> - Educational resources for cybersecurity professionals</li>
                <li><a href="https://futurecyberpros.com/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">Future Cyber Pros</a> - Career resources and guidance for the next generation of cybersecurity professionals</li>
                <li><a href="https://glossary.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">Cybersecurity Glossary</a> - Comprehensive dictionary of cybersecurity terms and definitions</li>
                <li><a href="https://events.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">Cyber Events</a> - Find cybersecurity conferences, workshops, and training events</li>
                <li><a href="https://cybertoons.fun/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500">Cyber Toons</a> - Engaging cybersecurity education through illustrations and comics</li>
              </ul>
            </dd>
          </div>
          
          <div className="bg-stone-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">References</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                  <div className="w-0 flex-1 flex items-center">
                    <span className="flex-1 w-0 truncate">MITRE ATT&CK Framework</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <a href="https://attack.mitre.org/" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-600 hover:text-purple-500">
                      Visit
                    </a>
                  </div>
                </li>
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                  <div className="w-0 flex-1 flex items-center">
                    <span className="flex-1 w-0 truncate">OWASP Top 10</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <a href="https://owasp.org/www-project-top-ten/" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-600 hover:text-purple-500">
                      Visit
                    </a>
                  </div>
                </li>
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                  <div className="w-0 flex-1 flex items-center">
                    <span className="flex-1 w-0 truncate">NIST Cybersecurity Framework</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <a href="https://www.nist.gov/cyberframework" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-600 hover:text-purple-500">
                      Visit
                    </a>
                  </div>
                </li>
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                  <div className="w-0 flex-1 flex items-center">
                    <span className="flex-1 w-0 truncate">CISA Cybersecurity Resources</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <a href="https://www.cisa.gov/topics/cybersecurity-best-practices" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-600 hover:text-purple-500">
                      Visit
                    </a>
                  </div>
                </li>
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                  <div className="w-0 flex-1 flex items-center">
                    <span className="flex-1 w-0 truncate">SANS Internet Storm Center</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <a href="https://isc.sans.edu/" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-600 hover:text-purple-500">
                      Visit
                    </a>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default About; 